(ns app.front.view.group.reporting.form-fields
  (:require [app.front.ui :as ui]
            [app.front.util :as futil]
            [app.front.view.group.admin.form-field-selection :as ff-selection]
            [app.front.view.group.reporting.common :as reporting-common]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(rf/reg-sub ::selected
  ;; This is a map of {int-index {:form "name" :field :key :label "field label"}}
  :-> ::selected)

(rf/reg-sub ::rows
  :<- [::selected]
  :<- [::ff-selection/raw-form-fields]
  (fn [[selected raw-form-fields] _]
    (->> (dissoc selected nil) ; Remove the not-yet-added row
         (p/map-vals (fn [{:keys [form field] :as row-vals}]
                       (let [field (or field ; When form select is changed, field select defaults to first option
                                       (-> raw-form-fields (get form) first))]
                         (merge row-vals (select-keys field [:field :label])))))
         (into {}))))

(rf/reg-sub ::sorted-selected-fields
  :<- [::rows]
  (fn [rows _]
    (->> rows
         (sort-by first)
         (mapv second))))

(rf/reg-sub ::selected-form-and-field
  :<- [::selected]
  (fn [selected [_ row-idx]]
    (get selected row-idx {})))

(rf/reg-event-db ::add-row
  (fn [db [_ form field label]]
    (update db
            ::selected
            #(let [existing-rows (dissoc % nil)
                   next-idx      (if (empty? existing-rows)
                                   0
                                   (->> existing-rows keys (apply max) inc))]
               (-> (dissoc % nil)
                   (assoc next-idx (util/mmap form field label)))))))

(rf/reg-event-db ::remove-row
  (fn [db [_ row-idx]]
    (p/dissoc-in db [::selected row-idx])))

(rf/reg-event-db ::select
  (fn [db [_ row-idx k v]]
    (cond-> db
      (= :form k) (update-in [::selected row-idx] dissoc :field :label)
      (nil? v)    (p/dissoc-in [::selected row-idx k])
      (some? v)   (assoc-in [::selected row-idx k] v))))

(rf/reg-event-db ::set-selected-from-filter
  (fn [db [_ {:keys [form-fields]}]]
    (->> form-fields
         (map-indexed vector)
         (into {})
         (assoc db ::selected))))

(rf/reg-sub ::invalid-form-selections
  :<- [::selected]
  :<- [::ff-selection/forms]
  (fn [[selected forms]]
    (->> selected
         (filter (fn [[_ {:keys [form]}]]
                   (-> forms set (contains? form) not)))
         (map first)
         set)))

(rf/reg-sub ::form-missing?
  :<- [::invalid-form-selections]
  (fn [invalid [_ row-idx]]
    (contains? invalid row-idx)))

(defn- form-field-row
  "A single row in the reporting form field selector, either for a new field or an already chosen one"
  [{:keys [row-idx tr single-select?]}]
  (let [{:keys [form field label]} (<sub [::selected-form-and-field row-idx])
        forms                      (<sub [::ff-selection/forms])
        form-fields                (<sub [::ff-selection/form-fields form])
        get-field                  #(p/find-first form-fields %)
        form-missing?              (<sub [::form-missing? row-idx])]
    [:div.mt-4.flex.flex-wrap
     [:div.mr-6.mb-4.w-64
      (cc/select
        {:id         (str "form-field-" row-idx)
         :label-text ""
         :hide-label true
         :class-name "full-width-select"
         :value      (if form-missing?
                       :none
                       (or form :none))
         :on-change  #(>evt [::select row-idx :form (futil/tval %)])}
        (when (or (nil? row-idx)
                  form-missing?)
          (cc/select-item {:key   "none"
                           :text  (tr [:reporting/select-form])
                           :value :none}))
        (futil/for-frag [source-form forms]
          (cc/select-item {:text  source-form
                           :value source-form})))]
     [:div.mb-4.w-64
      (cc/select
        {:id         (str "variable-source-field-" row-idx)
         :label-text ""
         :hide-label true
         :class-name "full-width-select"
         :value      (or (:index (get-field (util/mmap field label)))
                         :none)
         :disabled   (nil? form)
         :on-change  #(let [{:keys [field label]} (get-field {:index (futil/tval %)})]
                        (>evt [::select row-idx :field field])
                        (>evt [::select row-idx :label label]))}
        (when (nil? row-idx)
          (cc/select-item {:key   "none"
                           :text  (if (some? form)
                                    (tr [:reporting/select-field])
                                    (tr [:reporting/select-form-first]))
                           :value :none}))
        (futil/for-frag [source-field form-fields]
          (cc/select-item {:text  (ff-selection/form-field-text source-field)
                           :value (:index source-field)})))]
     (when form-missing?
       [:div.ml-6.-my-4 [ui/warning (tr [:reporting/missing-form-warning])]])
     (when-not single-select?
       [:div.ml-6.mb-4
        (if (nil? row-idx)
          (cc/button {:kind             :tertiary
                      :size             :field
                      :icon             i/plus
                      :icon-description ""
                      :disabled         (or (nil? form)
                                            (nil? field))
                      :on-click         #(>evt [::add-row form field label])}
                     (tr [:reporting/add-new-field]))
          (cc/button {:kind             :ghost
                      :size             :field
                      :icon             i/remove
                      :icon-position    :left
                      :icon-description ""
                      :on-click         #(>evt [::remove-row row-idx])}
                     (tr [:remove])))])]))

(defn form-field-selector
  "For selecting which form fields should be included in a report"
  [tr single-select?]
  (let [rows (<sub [::rows])]
    [:div.mt-2
     (when-not single-select? [reporting-common/selector-header tr :reporting/form-fields])
     (when-not (empty? rows)
       [:<>
        [:h4.mt-6.mb-4 (tr [:reporting/selected-fields])]
        (futil/for-frag [[row-idx _] (sort-by first rows)]
          [form-field-row (util/mmap tr row-idx single-select?)])
        [:hr.mt-6.border-dashed.border-t.border-ui-violet-50]])
     [:div.mt-6
      [form-field-row (util/mmap tr single-select?)]]]))
